import React from 'react'
import {
  Input as ChakraInput,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react'
import { FieldValues } from 'react-hook-form'
import { InputProps } from '../../types'
import { useInputProps } from '../../hooks/useInputProps'

export const Input = <TFormValues extends FieldValues>(
  props: InputProps<TFormValues>
) => {
  const inputProps = useInputProps(props)
  const {
    'data-qa': dataQa,
    className,
    error,
    helperText,
    isDisabled,
    isReadOnly,
    isRequired,
    label,
    leftElement,
    name,
    onBlur,
    onChange,
    onFocus,
    register,
    rightElement,
    rules,
    size = 'md',
    type = 'text',
    ...restProps
  } = inputProps

  const isError = !!error

  const errorColor = 'text-danger'
  const focusColor = 'text-primary'

  return (
    <FormControl
      data-qa={dataQa}
      id={name}
      isInvalid={isError}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      className={className}
      size={size}
      // Not sure why these handlers don't work
      // if added directly to the input component. I think
      // it has to do with the event handlers coming from react-hook-from.
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
    >
      <FormLabel>{label}</FormLabel>
      <InputGroup size={size}>
        {leftElement && (
          <InputLeftElement pointerEvents='none' children={leftElement} />
        )}
        <ChakraInput
          focusBorderColor={focusColor}
          errorBorderColor={errorColor}
          type={type}
          {...register?.(name, rules)}
          {...restProps}
        />
        {rightElement && (
          <InputRightElement pointerEvents='none' children={rightElement} />
        )}
      </InputGroup>
      {isError ? (
        <FormErrorMessage color={errorColor}>{error?.message}</FormErrorMessage>
      ) : (
        // If no helper text, we still want to display this element
        // so that the space for the error message is accounted for and we avoid
        // the inputs shifting in a form when errors appear
        <FormHelperText>{helperText || '\u200B'}</FormHelperText>
      )}
    </FormControl>
  )
}
